import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Box } from '@mui/material';

import { MasterIndexApi, MasterIndexApiPath } from '@/api';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { VerifiedFieldsProvider } from '@/contexts';
import { useKaseContext } from '@/hooks';
import { MasterNamePersonInformationSection } from '@/layouts/TabLayout/MasterNames/components/MasterNamePersonInformationSection';
import { IMasterNamePerson } from '@/models';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import MasterIndexPersonFlags from '@/pages/MasterIndex/Persons/Flags';
import { MasterIndexPersonNotes } from '@/pages/MasterIndex/Persons/Notes';

import { useKaseArrestsContext } from '../../../KaseArrestsContext';

interface ArresteePersonalInfoTabProps {
  viewMode?: boolean;
  canEditPerson?: boolean;
}

interface TabCounts {
  relatedRecords: number;
  flags: number;
  notes: number;
}

type PersonEditInnerTab = 'information' | 'related' | 'flags' | 'notes';

const ArresteePersonalInfoTab = ({
  viewMode = false,
  canEditPerson = true,
}: ArresteePersonalInfoTabProps) => {
  const { currentArrestee } = useKaseArrestsContext();
  const { isReadOnlyArrestees } = useKaseContext();
  const [activeInnerTab, setActiveInnerTab] =
    useState<PersonEditInnerTab>('information');
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    relatedRecords: 0,
    flags: 0,
    notes: 0,
  });
  const masterName = currentArrestee?.masterName as
    | IMasterNamePerson
    | undefined;
  const masterNameId = masterName?._id;
  const initialVerifiedFields = masterName?.idScanVerifiedFields || undefined;
  const isReadOnlyInformation = viewMode || !canEditPerson;

  const refreshTabCounts = useCallback(async () => {
    if (!masterNameId) return;

    try {
      const response = await MasterIndexApi.getPersonTabCounts(masterNameId);
      setTabCounts(response.data);
    } catch {
      setTabCounts({ relatedRecords: 0, flags: 0, notes: 0 });
    }
  }, [masterNameId]);

  useEffect(() => {
    void refreshTabCounts();
  }, [refreshTabCounts]);

  const innerTabs = useMemo(
    () => [
      {
        label: 'Person information',
        path: 'information',
        onClick: () => setActiveInnerTab('information'),
      },
      {
        label: 'Related records',
        path: 'related',
        disabled: !masterNameId,
        count: tabCounts.relatedRecords,
        onClick: () => setActiveInnerTab('related'),
      },
      {
        label: 'Flags',
        path: 'flags',
        disabled: !masterNameId,
        count: tabCounts.flags,
        onClick: () => setActiveInnerTab('flags'),
      },
      {
        label: 'Notes',
        path: 'notes',
        disabled: !masterNameId,
        count: tabCounts.notes,
        onClick: () => setActiveInnerTab('notes'),
      },
    ],
    [masterNameId, tabCounts],
  );

  return (
    <div className="w-full -mt-4">
      {!canEditPerson && (
        <Alert className="mb-4" severity="info" variant="outlined">
          You do not have permission to edit the linked Master Index person.
          Personal information is shown in read-only mode.
        </Alert>
      )}

      <div className="overflow-hidden bg-white border border-gray-200 rounded-xl">
        <Box
          sx={{
            '& .MuiButtonGroup-root': {
              my: 0,
              width: '100%',
              backgroundColor: 'transparent',
              borderRadius: 'inherit',
              overflow: 'hidden',
            },
          }}
        >
          <NavigationButtonGroup tabs={innerTabs} activeTab={activeInnerTab} />
        </Box>
      </div>

      <div className="mt-2">
        {/* Keep MasterNamePersonInformationSection mounted to preserve RHF form state across inner-tab navigation */}
        <div
          style={{
            display: activeInnerTab === 'information' ? undefined : 'none',
          }}
        >
          <VerifiedFieldsProvider initialVerifiedFields={initialVerifiedFields}>
            <MasterNamePersonInformationSection
              isMasterIndex={true}
              showLicenseInfo={true}
              showVerifiedBadges={false}
              disabled={isReadOnlyInformation}
              photosReadOnly={isReadOnlyArrestees}
              photosFieldName="masterNameFiles"
              fieldNameMap={{
                refNumber: 'masterNameRefNumber',
              }}
            />
          </VerifiedFieldsProvider>
        </div>

        {activeInnerTab === 'related' && masterNameId && (
          <div className="min-h-[480px]">
            <MasterIndexRelatedRecords
              type={MasterIndexApiPath.People}
              viewId={masterNameId}
            />
          </div>
        )}

        {activeInnerTab === 'flags' && masterNameId && (
          <div className="min-h-[480px]">
            <MasterIndexPersonFlags
              viewId={masterNameId}
              readOnly={viewMode}
              onFlagsUpdate={() => {
                void refreshTabCounts();
              }}
            />
          </div>
        )}

        {activeInnerTab === 'notes' && masterNameId && (
          <div className="min-h-[480px]">
            <MasterIndexPersonNotes
              viewId={masterNameId}
              readOnly={viewMode}
              onCountChange={() => {
                void refreshTabCounts();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ArresteePersonalInfoTab;
